import styled, { keyframes, css } from "styled-components";

const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(35, 206, 107, 0.45);
  }
  100% {
    box-shadow: 0 0 0 clamp(24px, 4vw, 110px) rgba(35, 206, 107, 0);
  }
`;

export const FloatingButton = styled.button<{ $isOpen?: boolean; $animated?: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, filter 0.25s;

  &:hover {
    filter: brightness(0.9);
    transform: scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    position: relative;
    z-index: 1;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    z-index: 0;
    ${(p) =>
      p.$animated && !p.$isOpen
        ? css`animation: ${pulseRing} 2.4s ease-out infinite;`
        : "display: none;"}
  }

  &::after {
    animation-delay: 1.2s;
  }
`;

export const ChatTooltip = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 32px;
  right: 90px;
  background: var(--card-bg);
  color: var(--card-text);
  border: 1px solid var(--card-border);
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 1.3rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  z-index: 9998;
  pointer-events: none;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateX(${(p) => (p.$visible ? "0" : "10px")});
  transition: opacity 0.35s ease, transform 0.35s ease;

  &::after {
    content: "";
    position: absolute;
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-left-color: var(--card-border);
  }
`;

export const ChatPanel = styled.div`
  position: fixed;
  bottom: 92px;
  right: 24px;
  z-index: 9998;
  width: 380px;
  max-height: 680px;
  background: var(--card-bg);
  color: var(--card-text);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    right: 16px;
    bottom: 88px;
    max-height: 80vh;
  }
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: var(--green);
  color: #fff;

  h3 {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 600;
  }
`;

export const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

export const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 400px;
  max-height: 640px;
`;

export const MessageBubble = styled.div<{ $role: "user" | "assistant" }>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 1.4rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  align-self: ${(p) => (p.$role === "user" ? "flex-end" : "flex-start")};
  background: ${(p) =>
    p.$role === "user" ? "var(--green)" : "var(--card-border)"};
  color: ${(p) => (p.$role === "user" ? "#fff" : "var(--card-text)")};

  a {
    color: ${(p) => (p.$role === "user" ? "#fff" : "var(--green)")};
    text-decoration: underline;
    word-break: break-word;
  }

  a:hover {
    opacity: 0.9;
  }
`;

export const ChatInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 8px;
  border-top: 1px solid var(--card-border);

  input {
    flex: 1;
    border: 1px solid var(--card-border);
    border-radius: 20px;
    padding: 8px 14px;
    font-size: 1.4rem;
    background: transparent;
    color: var(--card-text);
    outline: none;

    &::placeholder {
      color: var(--card-text);
      opacity: 0.5;
    }

    &:focus {
      border-color: var(--green);
    }
  }
`;

export const SendButton = styled.button`
  background: var(--green);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 1.4rem;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const WelcomeMessage = styled.div`
  text-align: center;
  font-size: 1.4rem;
  color: var(--card-text);
  opacity: 0.7;
  padding: 20px 10px;
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

export const TypingIndicator = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 4px;
  padding: 10px 14px;

  &::before,
  &::after,
  span {
    content: "";
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--card-text);
    opacity: 0.4;
    animation: ${bounce} 1.4s infinite ease-in-out both;
  }

  &::before {
    animation-delay: -0.32s;
  }

  span {
    animation-delay: -0.16s;
  }
`;
